import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Modal } from 'synfrastructure';
import { SubmissionError } from 'redux-form';
import numeral from 'numeral';
import { formatValidationErrors } from '../common/utils/getApiReducer';
import VideoPlayer from '../common/video/VideoPlayer';
import { editCourse, getCoursePreviewVideo, uploadVideo, getUnassignedBadges } from './actions';
import { addChapter, reorderChapters } from './chapters/actions';
import { selectActiveCourse, selectChapters } from './selectors';
import Icon from '../common/icons/Icon';
import AddForm from '../common/forms/AddForm';
import ChapterList from './chapters/ChapterList';
import CourseEditForm from './CourseEditForm';
import Breadcrumb from '../common/components/Breadcrumb';
import dataUrlToFile from '../common/utils/dataUrlToFile';
import SortableListWrapper from '../common/components/SortableListWrapper';

class CourseEditPage extends Component {
  static propTypes = {
    activeCourse: PropTypes.object,
    badges: PropTypes.object,
    courses: PropTypes.object,
    dispatch: PropTypes.func,
    initialValues: PropTypes.object,
  };

  constructor(props) {
    super(props);

    this.state = {
      addChapterModalVisible: false,
      clearIconImage: false,
      clearOrigImage: false,
      deleteChapterId: null,
      deleteChapterModalVisible: false,
      deleteChapterName: null,
      editingCourse: false,
    };
  }

  componentDidMount() {
    if (this.props.activeCourse.get('video_id') && !this.props.activeCourse.get('video')) {
      this.props.dispatch(getCoursePreviewVideo(this.props.match.params.courseId)).then(this.handleVideoResponse);
    }
    this.props.dispatch(getUnassignedBadges());
  }

  componentDidUpdate(prevProps, prevState) {
    if (!this.state.editingCourse && prevState.editingCourse !== this.state.editingCourse) {
      this.props.dispatch(getUnassignedBadges());
    }
  }

  toggleEditCourse = () => {
    this.setState(state => ({
      editingCourse: !state.editingCourse,
      clearOrigImage: false,
    }));
  };

  toggleAddChapterModal = () => {
    this.setState(state => ({ addChapterModalVisible: !state.addChapterModalVisible }));
  };

  onEditCourseSubmit = values => {
    const { dispatch, match: { params: { courseId } }, activeCourse } = this.props;
    const { clearOrigImage, clearIconImage } = this.state;
    if (values.image) {
      values.image = dataUrlToFile(values.image.imageResult, values.image.imageFilename);
    }

    if (values.icon) {
      values.icon = dataUrlToFile(values.icon.imageResult, values.icon.imageFilename);
    }

    if (!clearOrigImage) {
      values.image_id = activeCourse.get('image_id');
    }

    if (!clearIconImage) {
      values.icon_id = activeCourse.get('icon_id');
    }

    return dispatch(editCourse(courseId, values)).then(action => {
      if (!action.response.ok) {
        throw new SubmissionError(formatValidationErrors(action.json).toJS());
      } else {
        this.setState({ editingCourse: false });
      }
    });
  };

  checkVideoStatus = () => {
    const { dispatch, match: { params: { courseId } } } = this.props;

    dispatch(getCoursePreviewVideo(courseId)).then(this.handleVideoResponse);
  };

  handleVideoResponse = response => {
    if (response.json && response.json.transcode_status !== 'Complete' && response.json.transcode_status !== 'Error') {
      setTimeout(this.checkVideoStatus, 10000);
    }
  };

  onVideoUpload = file => {
    const { dispatch, match: { params: { courseId } } } = this.props;

    return dispatch(uploadVideo(courseId, file[0])).then(response => {
      setTimeout(this.checkVideoStatus, 10000);
    });
  };

  onAddChapterSubmit = values => {
    const { activeCourse, dispatch } = this.props;

    return dispatch(addChapter(activeCourse.get('id'), values)).then(action => {
      if (!action.response.ok) {
        throw new SubmissionError(formatValidationErrors(action.json).toJS());
      } else {
        this.props.history.push(`/courses/${activeCourse.get('id')}/chapters/${action.json.id}`);
      }
    });
  };

  removeIcon = () => {
    this.setState(state => ({
      clearIconImage: true,
    }));
  };

  removeImage = () => {
    this.setState(state => ({
      clearOrigImage: true,
    }));
  };

  renderChapters = () => {
    const { activeCourse, dispatch, chapters, match } = this.props;

    if (!activeCourse.get('published')) {
      return (
        <SortableListWrapper
          items={chapters}
          onSave={items => {
            return dispatch(reorderChapters({ chapterIds: items.map(chapter => chapter.id) }));
          }}
          title="Chapters"
        >
          <ChapterList courseId={match.params.courseId} />
        </SortableListWrapper>
      );
    } else {
      return <ChapterList courseId={match.params.courseId} />;
    }
  };

  render() {
    const { activeCourse, badges, initialValues, previewUploading, previewUploadProgress } = this.props;
    const { addChapterModalVisible, clearIconImage, clearOrigImage, editingCourse } = this.state;

    const breadcrumbs = [
      {
        label: 'Courses',
        to: '/courses',
      },
      {
        label: activeCourse.get('name'),
        to: null,
      },
    ];

    return (
      <div>
        <div className="admin-paper">
          <Modal isVisible={addChapterModalVisible} onClose={this.toggleAddChapterModal}>
            <AddForm
              entityType="Chapter"
              initialValues={{ name: null }}
              onSubmit={this.onAddChapterSubmit}
              onCancel={this.toggleAddChapterModal}
            />
          </Modal>
          <div className="admin-paper__header">
            <Breadcrumb crumbs={breadcrumbs} />
            <button onClick={this.toggleAddChapterModal} className="admin__add-button">
              <Icon icon="Plus" /> Add Chapter
            </button>
          </div>
          {editingCourse ? (
            <CourseEditForm
              badges={badges.get('data')}
              clearOrigImage={clearOrigImage}
              clearIconImage={clearIconImage}
              course={activeCourse}
              initialValues={initialValues}
              onSubmit={this.onEditCourseSubmit}
              onCancel={this.toggleEditCourse}
              onDelete={this.onDelete}
              onVideoUpload={this.onVideoUpload}
              previewUploading={previewUploading}
              previewUploadProgress={previewUploadProgress}
              removeIcon={this.removeIcon}
              removeImage={this.removeImage}
            />
          ) : (
            <div>
              <div className="admin-paper__edit-content" key="course-info">
                <div className="admin-paper__edit-content__row">
                  <p>Course Name</p>
                  <span className="title">{activeCourse.get('name')}</span>
                </div>
                <div className="admin-paper__edit-content__row">
                  <p>Course Status</p>
                  <span className="title">{activeCourse.get('published') ? 'Published' : 'Not Published'}</span>
                </div>
                <div className="admin-paper__edit-content__row">
                  <p>Short Description</p>
                  <span>
                    {activeCourse.get('description') ? activeCourse.get('description') : <em>No Description</em>}
                  </span>
                </div>
                <div className="admin-paper__edit-content__row">
                  <p>About the Course</p>
                  <span className="long-description">
                    {activeCourse.get('long_description') ? (
                      activeCourse.get('long_description')
                    ) : (
                      <em>No Description</em>
                    )}
                  </span>
                </div>
                <div className="admin-paper__edit-content__row">
                  <p>Course Price</p>
                  <span>{numeral(activeCourse.get('price') / 100).format('$0,0.00')}</span>
                </div>
                <div className="admin-paper__edit-content__row">
                  <p>Course Badge</p>
                  <span>{activeCourse.getIn(['badge', 'name']) || 'None'}</span>
                </div>
                <div className="admin-paper__edit-content__row">
                  <div className="admin-paper__edit-content__upload">
                    <p>Course Preview Video</p>
                    {activeCourse.get('video') ? (
                      <div className="admin__image-preview">
                        <VideoPlayer
                          video={activeCourse.get('video')}
                          key={`${activeCourse.getIn(['video', 'key'])}-${activeCourse.getIn([
                            'video',
                            'transcode_status',
                          ])}`}
                        />
                      </div>
                    ) : (
                      <div className="admin__image__placeholder image__placeholder">No Video</div>
                    )}
                  </div>
                  <div className="admin-paper__edit-content__upload">
                    <p>Course Image</p>
                    {activeCourse.getIn(['image', 'location']) ? (
                      <div
                        className="admin__image-preview"
                        style={{ backgroundImage: `url(${activeCourse.getIn(['image', 'location'])})` }}
                      />
                    ) : (
                      <div className="admin__image__placeholder image__placeholder">No Image</div>
                    )}
                  </div>
                  <div className="admin-paper__edit-content__upload">
                    <p>Course Icon</p>
                    {activeCourse.getIn(['icon', 'location']) ? (
                      <div
                        className="admin__image-preview"
                        style={{ backgroundImage: `url(${activeCourse.getIn(['icon', 'location'])})` }}
                      />
                    ) : (
                      <div className="admin__image__placeholder image__placeholder">No Icon</div>
                    )}
                  </div>
                </div>
                <div className="admin-paper__edit-button">
                  <button className="button" onClick={this.toggleEditCourse}>
                    Edit
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        {!editingCourse && this.renderChapters()}
      </div>
    );
  }
}

export default connect((state, ownProps) => {
  const activeCourse = selectActiveCourse(state.courses.courses, ownProps.match.params.courseId);

  return {
    activeCourse,
    badges: state.courses.badges,
    chapters: selectChapters(state),
    courses: state.courses.courses,
    previewUploading: state.courses.previewUploading,
    previewUploadProgress: state.courses.previewUploadProgress,
    initialValues: activeCourse
      ? {
          name: activeCourse.get('name'),
          description: activeCourse.get('description'),
          long_description: activeCourse.get('long_description'),
          price: activeCourse.get('price'),
          badge_id: activeCourse.get('badge_id') || '',
          published: activeCourse.get('published'),
        }
      : null,
  };
})(CourseEditPage);



// WEBPACK FOOTER //
// ./src/courses/CourseEditPage.js